import images from '@assets/images/index'
import { Col, Flex, Row } from 'antd'
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { LabelOptions } from 'chartjs-plugin-datalabels/types/options'
import { Bar } from 'react-chartjs-2'

import { Database_Procs_JTFByEntity_Item } from '@suntex.fim/api-client'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
)

const backGroundColors = ['#5645DA', '#83E2B8', '#3FB2FF', '#F9BF34', '#FF505A']

export const options: any = {
  responsive: true,
  plugins: {
    legend: {
      display: false
    },
    datalabels: {
      color: 'black',
      formatter: (value: string, context) => {
        if (!context.dataIndex) {
          // for all student removed %
          return value !== undefined ? `${value}\n` : ''
        }
        return value !== undefined ? `${value}\n` : '' // patch:bottom margin not applicable so added with lines
      },
      font: {
        size: 14,
        weight: 700
      },
      listeners: {
        enter(context: any) {
          context.hovered = true
          return true
        },
        leave(context: any) {
          context.hovered = false
          return true
        }
      },
      anchor: 'end'
    } as LabelOptions,
    tooltip: {
      filter: (context: any) => {
        return (
          !(context.dataset?.hide && context.dataIndex > 0) ||
          !context.dataset?.hide
        )
      },
      borderColor: '#D2D2D2',
      borderWidth: 1,
      displayColors: false,
      callbacks: {
        label: (context: any) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const label = context.raw || 0
          if (context.label === 'All Students') {
            return label
          }
          return ` ${label}`
        },
        title: (context: any) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const label = context[0]?.dataset.label || ''
          if (context[0]?.label === 'All Students') {
            return context[0].label || ''
          }
          return label
        }
      },
      bodyAlign: 'center',
      backgroundColor: 'white',
      titleColor: 'black',
      bodyColor: 'black',
      bodyFont: {
        size: 14,
        weight: 700
      }
    }
  },
  maintainAspectRatio: false,
  layout: { autoPadding: true, padding: { top: 20 } },
  scales: {
    x: {
      ticks: {
        color: 'black',
        font: {
          size: 14,
          weight: 700
        }
      }
    }
  },
  skipNull: true,
  interaction: {
    intersect: false,
    mode: 'nearest',
    axis: 'x'
  }
}

const labels = ['All Students', 'Pre-Test', 'Pre & Post-Test']

/**
 * returns JSX for assessment just the facts chart
 */
function AssessmentJTFCharts({
  chartData = []
}: {
  chartData: Database_Procs_JTFByEntity_Item[]
}) {
  const sortedData = [{ ...chartData[0], tierid: 0 }, ...chartData].sort(
    (a, b) => {
      const aTierId = a.tierid
      const bTierId = b.tierid
      return aTierId - bTierId
    }
  )
  const data: ChartData<'bar', Array<number | undefined>, string> = {
    labels,
    datasets: sortedData.map(
      ({
        tier,
        tierid,
        totalstudentsplaying = 0,
        numberpretest = 0,
        numberposttest = 0
      }) => {
        if (tierid === 0) {
          return {
            data: [totalstudentsplaying, undefined, undefined],
            backgroundColor: backGroundColors[tierid],
            borderRadius: 6,
            borderSkipped: false,
            hide: true
          }
        }
        return {
          label: tier,
          data: [undefined, numberpretest, numberposttest],
          backgroundColor: backGroundColors[tierid || 0],
          borderRadius: 6,
          borderSkipped: false
        }
      }
    )
  }
  const GUTTER_HORIZONTAL = 30
  const GUTTER_VERTICAL = 20
  return (
    <Row gutter={[GUTTER_HORIZONTAL, GUTTER_VERTICAL]} align="middle">
      <Col xs={{ flex: '100%' }} sm={{ flex: '1' }} className="bar-chart">
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
        <Bar options={options} data={data} />
      </Col>
      <Col
        xs={{ flex: '100%' }}
        sm={{ flex: '0 1 160px' }}
        className="bar-legend"
      >
        <ul className="bar-legend-keys">
          <li className="color-keys">
            <Flex className="key-item" align="baseline" gap="6px">
              <div className="indicator">
                <images.FluentIndicator />
              </div>
              <div className="indicator-desc">
                <div className="indicator-title fw-bold">Fluent</div>
                <div className="indicator-type">95-100% Correct</div>
              </div>
            </Flex>
          </li>
          <li className="color-keys">
            <Flex className="key-item" align="baseline" gap="6px">
              <div className="indicator">
                <images.ProficientIndicator />
              </div>
              <div className="indicator-desc">
                <div className="indicator-title fw-bold">Proficient</div>
                <div className="indicator-type">80-94% Correct</div>
              </div>
            </Flex>
          </li>
          <li className="color-keys">
            <Flex className="key-item" align="baseline" gap="6px">
              <div className="indicator">
                <images.EmergingIndiCator />
              </div>
              <div className="indicator-desc">
                <div className="indicator-title fw-bold">Emerging</div>
                <div className="indicator-type">65-79% Correct</div>
              </div>
            </Flex>
          </li>
          <li className="color-keys">
            <Flex className="key-item" align="baseline" gap="6px">
              <div className="indicator">
                <images.NeedHelpIndicator />
              </div>
              <div className="indicator-desc">
                <div className="indicator-title fw-bold">Needs Help</div>
                <div className="indicator-type">0-64% Correct</div>
              </div>
            </Flex>
          </li>
          <li className="color-keys">
            <Flex className="key-item" align="baseline" gap="6px">
              <div className="indicator">
                <images.AllStudentsIndicator />
              </div>
              <div className="indicator-desc">
                <div className="indicator-title fw-bold">All Students</div>
              </div>
            </Flex>
          </li>
        </ul>
      </Col>
    </Row>
  )
}

export default AssessmentJTFCharts
